import { Amplitude, Events, Tracking } from '@iwoca/frontend-tracking-library';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import qs from 'qs';
import { getCookie } from 'utils/cookieHelper';
import { URLS } from 'utils/tests/urls.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';

import { TApplicationData, TProductTypes } from './applications.types';
import { TGetOffersResponse } from './lapiOverrides.types';
import {
  TGetDocumentsResponse,
  TGetCompanyLegalFormResponse,
} from './lendingApi.types';

export const getDESignupImpersonationUrl = (stateKey: string) =>
  `/konto/anmeldung/?state_key=${stateKey}&viewing_as=introducer`;

type UpdateWithdrawalFee = {
  offerId: string;
  applicationId: string;
  introducerId: string | undefined;
  feeRate: number;
  setLoading: (isLoading: boolean) => void;
  onSuccess: () => void;
  onError: () => void;
};

export async function lendingAPIPostRefreshApplication({
  stateKey,
  amount,
  productType,
  loanDuration,
  introducerId,
}: Omit<
  TApplicationData & { productType: TProductTypes },
  'loanPurpose' | 'loanDuration' | 'amount'
> & {
  amount: number;
  introducerId: string;
  loanDuration?: { amount: number; unit: string };
}) {
  const requestedProduct = {
    amount,
    product_type: productType,
    purpose: undefined,
    duration: loanDuration,
  };

  return await lendingApiFetch(`${URLS.refreshApplication({ stateKey })}`, {
    method: 'post',
    body: JSON.stringify({
      data: {
        introducer: {
          entity_to_contact: 'introducer',
          introducer_id: introducerId,
        },
        requested_products: [requestedProduct],
      },
    }),
  });
}

export async function lendingAPIGetDocuments(
  state_key: string,
): Promise<TGetDocumentsResponse> {
  const response = await lendingApiFetchJson(
    URLS.getDocumentUpload({ stateKey: state_key }),
  );
  return response;
}

export const getLoginUrl = () => `/login/passwordless/email`;

async function redirectOnError() {
  try {
    const profileResponse = await lendingApiFetchJson(URLS.profile());

    if (profileResponse.data.user_type === 'staff') {
      return;
    }
    throw new Error('error fetching context');
  } catch {
    if (
      (process.env.NODE_ENV === 'production' ||
        process.env.NODE_ENV === 'test') &&
      typeof window !== 'undefined'
    ) {
      window.location.assign(getLoginUrl());
    }
  }
}

const getImpersonatingIntroducerId = () => {
  // eslint-disable-next-line import/no-named-as-default-member
  const searchParams = qs.parse(window?.location?.search?.substring(1));
  return searchParams?.__impersonate as string | undefined;
};

const getImpersonatingContextUrl = async (introducerId: string) => {
  if (introducerId.includes('-')) {
    // Remove existing impersonation first
    await URLS.introducerContext({ query: { __impersonate: 0 } });
    return URLS.introducerContextWithId({ introducerId });
  }

  return URLS.introducerContext({ query: { __impersonate: introducerId } });
};

export const getIntroducerContext = async () => {
  const impersonatingIntroducerId = getImpersonatingIntroducerId();
  if (impersonatingIntroducerId) {
    return getImpersonatingIntroducerContext(impersonatingIntroducerId);
  }

  const response = await lendingApiFetchJson(
    URLS.introducerContext({}),
    {},
    {},
    redirectOnError,
  );

  return response;
};

const getImpersonatingIntroducerContext = async (introducerId: string) => {
  const impersonatingContextUrl =
    await getImpersonatingContextUrl(introducerId);

  const response = await lendingApiFetch(impersonatingContextUrl);

  if (response.ok) {
    return response.json();
  }

  if (response.status === 403) {
    const profileResponse = await lendingApiFetchJson(URLS.profile());
    if (!!profileResponse.data.impersonator_type) {
      const unimpersonateResponse = await lendingApiFetch(
        '/my_account/loan/?__unimpersonate=0',
      );
      if (!unimpersonateResponse.ok && unimpersonateResponse.status !== 403) {
        throw Error(unimpersonateResponse.statusText);
      }

      const contextRetryResponse = await lendingApiFetchJson(
        impersonatingContextUrl,
      );

      return contextRetryResponse;
    }
  }

  throw Error(response.statusText);
};

export async function lendingAPIPutWithdrawalFee({
  introducerId,
  applicationId,
  offerId,
  feeRate,
  onError,
  onSuccess,
  setLoading,
}: UpdateWithdrawalFee) {
  setLoading(true);
  const response = await lendingApiFetch(
    URLS.withdrawalFeeRate({ introducerId, applicationId, offerId }),
    {
      method: 'PUT',
      body: JSON.stringify({
        data: {
          withdrawal_fee_rate: feeRate,
        },
      }),
    },
  );

  if (!response.ok) {
    onError();
  } else {
    onSuccess();
  }

  setLoading(false);

  return response;
}

type ReviewOfferArgs = {
  offerId: string;
  introducerId: string;
  applicationId: string;
  phoneNumber: string | null;
  contactTime?: string | null;
  asap?: boolean;
  setLoading: (isLoading: boolean) => void;
  onSuccess: () => void;
  onError: () => void;
};

type ReviewOfferBody = {
  entity_to_contact: string;
  suggested_contact_phone_number: string;
  suggested_contact_asap?: boolean;
  suggested_contact_time?: string;
};

export const reviewOffer = async ({
  offerId,
  introducerId,
  applicationId,
  phoneNumber,
  contactTime,
  asap = false,
  setLoading,
  onSuccess,
  onError,
}: ReviewOfferArgs) => {
  Tracking.trackClick({ identifier: 'Confirm Funding Button' });
  setLoading(true);

  const reviewBody = {
    entity_to_contact: 'customer',
    suggested_contact_phone_number: phoneNumber,
  } as ReviewOfferBody;

  if (asap) {
    reviewBody.suggested_contact_asap = true;
  }

  if (contactTime) {
    reviewBody.suggested_contact_time = contactTime;
  }

  const response = await lendingApiFetch(
    URLS.offerEntityToContact({ introducerId, applicationId, offerId }),
    {
      method: 'put',
      body: JSON.stringify({ data: reviewBody }),
    },
  );

  if (!response.ok) {
    Tracking.trackEvent({ event: 'Confirm Funding [Error]]' });
    onError();
  } else {
    Tracking.trackEvent({ event: 'Confirm Funding [Success]' });
    onSuccess();
  }

  setLoading(false);
  return response;
};

// TODO Delete below references to rejecting an individual offer when we move over to MFOs
// we should then be able to just use 'rejectOffers' for all instances
type TRejectOfferArgs = {
  offerId: string;
  introducerId: string | undefined;
  applicationId: string;
  rejectReason: string | undefined;
  stateKey: string;
  locale: string;
  setLoading: (isLoading: boolean) => void;
  onSuccess: () => void;
  onError: () => void;
};

export const rejectOffer = async ({
  offerId,
  introducerId,
  applicationId,
  rejectReason,
  stateKey,
  locale,
  setLoading,
  onSuccess,
  onError,
}: TRejectOfferArgs) => {
  Tracking.trackClick({ identifier: 'Reject Offer Button' });
  setLoading(true);

  try {
    await lendingApiFetch(
      URLS.offerEntityToContact({
        introducerId: introducerId as string,
        applicationId,
        offerId,
      }),
      {
        method: 'put',
        body: JSON.stringify({ data: { entity_to_contact: 'no_one' } }),
      },
    );

    await lendingApiFetch(URLS.rejectOfferReasonsEdge({ stateKey }), {
      method: 'post',
      body: JSON.stringify({
        data: [{ formal_offer_id: offerId, reason: rejectReason }],
      }),
    });

    Amplitude.track(
      Events.introducerPortal.introducerPortalEventCreator({
        event: AMPLITUDE_EVENTS.offerRejected,
        locale,
        payload: { rejectReason: rejectReason },
      }),
    );

    Tracking.trackEvent({ event: 'Reject Offer [Success]' });

    onSuccess();
  } catch (error) {
    Tracking.trackEvent({ event: 'Reject Offer [Error]]' });
    onError();
  }

  setLoading(false);
};

export function fetchGetCompanySearch<T = GetCompanySearchResponse>({
  companyName,
}: {
  companyName: string;
}): Promise<T> {
  return lendingApiFetchJson(URLS.companySearch({ companyName }));
}

export function fetchGetCompanyLegalForm({
  crefoId,
}: {
  crefoId: string;
}): Promise<TGetCompanyLegalFormResponse> {
  return lendingApiFetchJson(
    URLS.getLegalForm({ query: { crefo_id: crefoId } }),
  );
}

export async function fetchSaveUploadedDocuments({
  stateKey,
  body,
}: {
  stateKey: string;
  body: FormData;
}) {
  const response = await lendingApiFetchWithoutContentType(
    URLS.getDocumentUpload({ stateKey }),
    { method: 'POST', body },
    { enctype: 'multipart/form-data' },
  );
  return await response.json();
}

export const fetchGetDrawDownRepaymentSchedule = async (
  productType: TProductTypes,
  stateKey: string,
  offerId: string,
  amount: number,
  date?: string,
) => {
  const res = await lendingApiFetch(
    URLS.getDrawdownRepaymentSchedule({
      stateKey,
      product: productType,
      offerId,
      query: { amount, early_final_repayment_date: date },
    }),
  );

  if (!res.ok) {
    const response = await res.json();
    throw response.errors[0].detail;
  }
  return await res.json();
};

export async function fetchGetIntroducerPortalApiOffers(
  introducer_id: string,
  application_id: string,
): Promise<TGetOffersResponse> {
  const response = await lendingApiFetchJson(
    URLS.getOffers({
      introducerId: introducer_id,
      applicationId: application_id,
    }),
  );
  return response;
}

export const lendingApiFetchJson = async (
  url: string,
  options = {},
  headers = {},
  errorHandler = () => {},
  customSuccessResponse: any = null,
) => {
  const response = await lendingApiFetch(url, options, headers);

  if (!response.ok) {
    errorHandler && errorHandler();
    throw Error(response.statusText);
  }

  return customSuccessResponse || response.json();
};

const API_AUTH_TOKEN = 'Token iwoca-9e4606290c6294a6b40e15c9bb75cd2daf0';

const lendingApiFetch = (url: string, options = {}, headers = {}) =>
  fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      authorization: API_AUTH_TOKEN,
      'x-csrftoken': getCookie('csrftoken') as string,
      ...headers,
    },
  });

const lendingApiFetchWithoutContentType = (
  url: string,
  options = {},
  headers = {},
) =>
  fetch(url, {
    ...options,
    headers: {
      authorization: API_AUTH_TOKEN,
      'x-csrftoken': getCookie('csrftoken') as string,
      ...headers,
    },
  });
